import { useState } from "react";
import { Link, useLocation } from "wouter";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { JobHubSkeleton } from "@/components/ui/JobHubSkeleton";
import { JobKPICards } from "@/components/jobs/JobKPICards";
import { JobFiltersSection } from "@/components/jobs/JobFiltersSection";
import { JobForm } from "@/components/jobs/JobForm";
import { PauseJobModal } from "@/components/jobs/PauseJobModal";
import { ActivateJobModal } from "@/components/jobs/ActivateJobModal";
import { useJobOperations } from "@/hooks/useJobOperations";
import { UserRole } from "@/lib/schema";
import { 
  JobHubJob,
  getStatusColor, 
  getPriorityColor, 
  getPriorityIcon,
  filterJobs
} from "@/lib/jobUtils";
import { 
  JobFormState,
  getInitialJobFormState,
  jobToFormState,
  formStateToJobData
} from "@/lib/jobFormUtils";
import { COMMON_CLASSES } from "@/lib/jobConstants";
import { 
  Plus,
  Edit,
  Pause,
  Play,
  Building2,
  Star,
  AlertCircle,
  Eye,
  Users
} from "lucide-react";
import jobsIcon from "@/assets/dashboard/sidebar/jobs.svg";

export default function JobHub() {
  const { userData, currentUser } = useAuth();
  const [_, setLocation] = useLocation();
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  
  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingJob, setEditingJob] = useState<JobHubJob | null>(null);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [actionJob, setActionJob] = useState<JobHubJob | null>(null);
  
  // Form state
  const [jobForm, setJobForm] = useState<JobFormState>(getInitialJobFormState());
  
  // Job operations hook
  const {
    jobsData,
    loading,
    error,
    fetchJobs,
    handleBulkAction,
    handleJobStatusChange,
    handleUpdateJob,
    clearError
  } = useJobOperations({ userData, currentUser });

  // Filter jobs based on search and filter criteria
  const filteredJobs = filterJobs(jobsData, searchTerm, statusFilter, priorityFilter);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedJobs(filteredJobs.map(job => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleSelectJob = (jobId: string, checked: boolean) => {
    if (checked) {
      setSelectedJobs([...selectedJobs, jobId]);
    } else {
      setSelectedJobs(selectedJobs.filter(id => id !== jobId));
    }
  };

  const onBulkAction = async (action: string) => {
    await handleBulkAction(action, selectedJobs);
    setSelectedJobs([]);
  };


  const navigateToCreateJob = () => {
    setLocation("/admin/jobs/create");
  };


  const handleJobAction = async (jobId: string, action: string) => {
    const job = jobsData.find(j => j.id === jobId);
    if (!job) return;

    if (action === 'edit') {
      setEditingJob(job);
      setJobForm(jobToFormState(job));
      setShowEditModal(true);
    } else if (action === 'pause') {
      setActionJob(job);
      setShowPauseDialog(true);
    } else if (action === 'activate') {
      setActionJob(job);
      setShowActivateDialog(true);
    }
  };

  const handleConfirmPause = async () => {
    if (!actionJob) return;
    setShowPauseDialog(false);
    await handleJobStatusChange(actionJob.id, 'pause');
    setActionJob(null);
  };

  const handleConfirmActivate = async () => {
    if (!actionJob) return;
    setShowActivateDialog(false);
    await handleJobStatusChange(actionJob.id, 'activate');
    setActionJob(null);
  };

  const handleSaveEditJob = async (formData: JobFormState, recruiters?: Array<{id: string, firstName: string, lastName: string}>): Promise<boolean> => {
    if (!editingJob) return false;
    
    const updates = formStateToJobData(formData, recruiters);
    const success = await handleUpdateJob(editingJob.id, updates);
    
    if (success) {
      setShowEditModal(false);
      setEditingJob(null);
      setJobForm(getInitialJobFormState());
    }
    
    return success;
  };

  if (loading) {
    return (
      <SuperAdminLayout 
        title="Job Hub" 
        subtitle="Manage job postings, assignments, and recruitment workflows."
      >
        <JobHubSkeleton />
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout 
      title="Job Hub" 
      subtitle="Manage job postings, assignments, and recruitment workflows."
    >
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className={`text-red-600 ${COMMON_CLASSES.text.input}`}>{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { clearError(); fetchJobs(); }}
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      )}
      
      {/* Header Actions */}
      <div className="flex justify-end mb-6 lg:mb-8">
        {userData?.role === UserRole.SUPER_ADMIN && (
          <Button 
            variant="primary"
            size="sm"
            onClick={navigateToCreateJob}
            data-testid="button-create-job"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Job
          </Button>
        )}
        
        {/* Edit Job Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent 
            className="max-w-4xl max-h-[90vh] overflow-y-auto dialog-content-wrapper" 
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onPointerDownOutside={(e) => {
              // Allow clicks on Radix Select components (which render in portals)
              const target = e.target as HTMLElement;
              if (
                target.closest('[data-radix-select-content]') ||
                target.closest('[data-radix-select-viewport]') ||
                target.closest('[data-radix-select-item]') ||
                target.closest('[data-radix-portal]')
              ) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="space-grotesk text-xl font-bold">Edit Job</DialogTitle>
              <DialogDescription className="space-grotesk text-gray-600">
                Update the job information and settings below.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <JobForm
                initialData={jobForm}
                onSubmit={handleSaveEditJob}
                onCancel={() => setShowEditModal(false)}
                submitButtonText="Save Changes"
                showHeader={false}
              />
            </div>
            <style>{`
              /* Force z-index and pointer events for Select components */
              [data-radix-select-content],
              [data-radix-select-viewport],
              [data-radix-select-item],
              [data-radix-portal] {
                z-index: 200 !important;
                pointer-events: auto !important;
              }

              /* Prevent scroll on Select trigger focus */
              .dialog-content-wrapper [data-radix-select-trigger],
              .dialog-content-wrapper [data-radix-select-trigger]:focus,
              .dialog-content-wrapper [data-radix-select-trigger]:focus-visible {
                scroll-margin: 0 !important;
                scroll-padding: 0 !important;
                scroll-behavior: auto !important;
              }
            `}</style>
          </DialogContent>
        </Dialog>
      </div>

          {/* Quick Stats */}
          <JobKPICards jobs={jobsData} loading={loading} />

          {/* Filters and Search */}
          <JobFiltersSection
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            priorityFilter={priorityFilter}
            setPriorityFilter={setPriorityFilter}
            selectedJobs={selectedJobs}
            onBulkAction={onBulkAction}
          />

          {/* Jobs Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F6F6F6] border border-[#EAEAEA]">
                      <TableHead className="w-12 py-3 px-4">
                        <Checkbox
                          checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="checkbox-select-all"
                        />
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal py-3 px-4 min-w-[300px]">
                        Job Title
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal py-3 px-4 min-w-[100px]">
                        Status
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal py-3 px-4 min-w-[100px]">
                        Priority
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal py-3 px-4 min-w-[100px]">
                        Applications
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal py-3 px-4 min-w-[140px]">
                        Assigned Recruiter
                      </TableHead>
                      <TableHead className="text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal py-3 px-4 min-w-[100px]">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobs.map((job) => (
                      <TableRow key={job.id} className={`border-b border-gray-200 hover:bg-gray-50 ${job.status === 'filled' ? 'bg-orange-100' : ''}`} data-testid={`job-row-${job.id}`}>
                        <TableCell className="py-3 px-4">
                          <Checkbox
                            checked={selectedJobs.includes(job.id)}
                            onCheckedChange={(checked) => handleSelectJob(job.id, checked as boolean)}
                            data-testid={`checkbox-job-${job.id}`}
                          />
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <div>
                            <Link href={`/admin/jobs/${job.id}`}>
                              <h4 className="text-sm font-medium text-gray-900 space-grotesk mb-1 hover:text-orange-600 cursor-pointer">
                                {job.title}
                              </h4>
                            </Link>
                            <p className="text-xs text-gray-500 capitalize">{job.category}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <Badge className="bg-gray-100 text-gray-700 border-0 text-xs">
                                ID: {job.internalJobId}
                              </Badge>
                              <Badge className="bg-blue-100 text-blue-700 border-0 text-xs capitalize">
                                {job.employmentType}
                              </Badge>
                              {job.skills.slice(0, 2).map((skill, index) => (
                                <Badge key={index} className="bg-orange-100 text-orange-700 border-0 space-grotesk text-xs">
                                  {skill}
                                </Badge>
                              ))}
                              {job.skills.length > 2 && (
                                <Badge className="bg-gray-100 text-gray-700 border-0 space-grotesk text-xs">
                                  +{job.skills.length - 2}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <Badge className={`${getStatusColor(job.status)} border-0 text-xs font-medium capitalize`}>
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <Badge className={`${getPriorityColor(job.priority)} border-0 text-xs font-medium flex items-center w-fit capitalize`}>
                            {getPriorityIcon(job.priority) === 'AlertCircle' && <AlertCircle className="h-3 w-3 mr-1" />}
                            {getPriorityIcon(job.priority) === 'Star' && <Star className="h-3 w-3 mr-1" />}
                            {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900 space-grotesk">{job.applications}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <span className="text-sm text-gray-900">
                            {(job.assignedRecruiter && typeof job.assignedRecruiter === 'object') ? job.assignedRecruiter.name : job.assignedRecruiter || 'Unassigned'}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Link href={`/admin/jobs/${job.id}`}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-gray-600 hover:bg-gray-100"
                                data-testid={`button-view-job-${job.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleJobAction(job.id, 'edit')}
                              className="h-8 px-2 text-gray-600 hover:bg-gray-100"
                              data-testid={`button-edit-job-${job.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {job.status !== 'filled' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleJobAction(job.id, job.status === 'active' ? 'pause' : 'activate')}
                                className="h-8 px-2 text-gray-600 hover:bg-gray-100"
                                data-testid={`button-toggle-job-${job.id}`}
                              >
                                {job.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {filteredJobs.length === 0 && (
                <div className="text-center py-12">
                  <img src={jobsIcon} alt="Jobs" className="h-12 w-12 mx-auto mb-4 opacity-40" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
                  <p className="text-gray-600">Try adjusting your filters or search terms.</p>
                </div>
              )}
            </div>

          {/* Pause Job Confirmation Dialog */}
          <PauseJobModal
            open={showPauseDialog}
            onOpenChange={setShowPauseDialog}
            job={actionJob}
            onConfirm={handleConfirmPause}
          />

          {/* Activate Job Confirmation Dialog */}
          <ActivateJobModal
            open={showActivateDialog}
            onOpenChange={setShowActivateDialog}
            job={actionJob}
            onConfirm={handleConfirmActivate}
          />
    </SuperAdminLayout>
  );
}